/**
 * Video Texture Manager
 *
 * Simplified manager that tracks video elements for cleanup purposes.
 * PixiJS v8 automatically updates video textures when autoUpdate=true is set on the texture source.
 * The shared PixiJS ticker handles all texture updates - we just manage video lifecycle.
 */

export class VideoTextureManager {
  constructor(pixiApp) {
    this.pixiApp = pixiApp;
    this.videoSprites = new Map(); // Map of elementId -> { sprite, videoElement }
  }

  /**
   * Register a video sprite
   * @param {string} elementId - Unique identifier for the element
   * @param {PIXI.Sprite} sprite - The PixiJS sprite
   * @param {HTMLVideoElement} videoElement - The video element
   */
  registerVideo(elementId, sprite, videoElement) {
    if (!elementId || !sprite || !videoElement) {
      console.warn('Invalid parameters for registerVideo');
      return;
    }

    // Ensure autoUpdate is enabled on the texture source
    // This tells PixiJS to automatically update the texture via the shared ticker
    if (sprite.texture && sprite.texture.source && 'autoUpdate' in sprite.texture.source) {
      sprite.texture.source.autoUpdate = true;
      // console.log(`✅ Enabled autoUpdate for video ${elementId}`);
    }

    this.videoSprites.set(elementId, {
      sprite,
      videoElement
    });

    // console.log(`Registered video ${elementId} - PixiJS will auto-update via shared ticker`);
  }

  /**
   * Unregister a video sprite
   * @param {string} elementId - Unique identifier for the element
   */
  unregisterVideo(elementId) {
    const videoData = this.videoSprites.get(elementId);
    if (videoData) {
      // Pause the video
      if (videoData.videoElement && !videoData.videoElement.paused) {
        videoData.videoElement.pause();
      }
      
      this.videoSprites.delete(elementId);
      // console.log(`Unregistered video ${elementId}`);
    }
  }

  /**
   * Clean up all resources
   */
  destroy() {
    // Pause and clean up all videos
    this.videoSprites.forEach((videoData, elementId) => {
      if (videoData.videoElement && !videoData.videoElement.paused) {
        videoData.videoElement.pause();
      }
    });
    
    this.videoSprites.clear();
    this.pixiApp = null;
    
    console.log('Video texture manager destroyed');
  }

  /**
   * Get statistics about managed videos
   */
  getStats() {
    const total = this.videoSprites.size;
    let playing = 0;

    this.videoSprites.forEach((videoData) => {
      if (videoData.videoElement && !videoData.videoElement.paused) playing++;
    });

    return {
      total,
      playing
    };
  }
}

/**
 * Create a video element optimized for PixiJS texture updates
 * Note: This function is kept for compatibility but is not currently used.
 * Videos are now loaded directly through PIXI.Assets.load() which creates
 * the video element automatically with optimal settings.
 *
 * @param {string} videoUrl - URL of the video
 * @returns {HTMLVideoElement}
 */
export function createOptimizedVideoElement(videoUrl) {
  const video = document.createElement('video');
  
  // Set attributes for optimal performance
  video.src = videoUrl;
  video.crossOrigin = 'anonymous';
  video.preload = 'auto';
  video.playsInline = true;
  video.muted = true; // Start muted, will be controlled by element state
  video.loop = false;
  
  // Hide from DOM
  video.style.display = 'none';
  
  return video;
}